import React, { useState, useEffect, useCallback } from 'react';
import {
  FiSearch, FiEdit2, FiX, FiCheck,
  FiCalendar, FiUser, FiPhone, FiMail, FiBriefcase,
  FiMessageSquare, FiChevronLeft, FiChevronRight,
} from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { formatINR } from '../utils/pricing';
import './SalesOrders.css';

// ─────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'all',       label: 'All',       color: '#6b7280' },
  { value: 'new',       label: 'New',       color: '#3b82f6' },
  { value: 'contacted', label: 'Contacted', color: '#f59e0b' },
  { value: 'quoted',    label: 'Quoted',    color: '#8b5cf6' },
  { value: 'converted', label: 'Converted', color: '#10b981' },
  { value: 'closed',    label: 'Closed',    color: '#6b7280' },
];

const STATUS_MAP = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s]));

function StatusBadge({ status }) {
  const cfg = STATUS_MAP[status] || STATUS_MAP.new;
  return (
    <span className="so-badge" style={{
      background: cfg.color + '18',
      color: cfg.color,
      border: `1px solid ${cfg.color}40`,
    }}>
      {cfg.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Detail / Edit Modal
// ─────────────────────────────────────────────────────────────────────
function QuoteDetailModal({ quote: initialQuote, onClose, onUpdated }) {
  const [quote,    setQuote]    = useState(initialQuote);
  const [editing,  setEditing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [editForm, setEditForm] = useState({
    customerName:         initialQuote.customerName,
    customerPhone:        initialQuote.customerPhone,
    customerEmail:        initialQuote.customerEmail || '',
    customerCompany:      initialQuote.customerCompany || '',
    customerMessage:      initialQuote.customerMessage || '',
    expectedDeliveryDate: initialQuote.expectedDeliveryDate || '',
    status:               initialQuote.status,
    adminNotes:           initialQuote.adminNotes || '',
    items:                JSON.parse(JSON.stringify(initialQuote.items || [])),
  });

  const setField = (field) => (e) => setEditForm(f => ({ ...f, [field]: e.target.value }));

  const updateItem = (idx, field, value) => {
    setEditForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      const qty   = parseFloat(items[idx].quantity)  || 0;
      const price = parseFloat(items[idx].unitPrice)  || 0;
      items[idx].totalPrice = qty * price;
      return { ...f, items };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await API.put(`/public-quotes/admin/${quote.id}`, { ...editForm, items: editForm.items });
      if (res.data.success) {
        setQuote(res.data.quote);
        setEditing(false);
        toast.success('Quote updated');
        onUpdated(res.data.quote);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update quote');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusQuickChange = async (newStatus) => {
    try {
      const res = await API.put(`/public-quotes/admin/${quote.id}`, { status: newStatus });
      if (res.data.success) {
        setQuote(res.data.quote);
        setEditForm(f => ({ ...f, status: newStatus }));
        toast.success('Status updated');
        onUpdated(res.data.quote);
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const displayItems = editing ? editForm.items : (quote.items || []);
  const subtotal = displayItems.reduce((s, i) => s + (parseFloat(i.totalPrice) || 0), 0);

  const labelStyle = {
    fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.05em', color: '#6b7280', marginBottom: 4,
  };
  const inputStyle = {
    width: '100%', padding: '8px 10px', fontSize: '0.88rem',
    border: '1px solid var(--border)', borderRadius: 8,
    background: '#fff', boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.4)', display: 'flex',
        alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px 16px', overflowY: 'auto',
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 760,
        padding: 28, position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', fontFamily: 'monospace' }}>{quote.referenceNumber}</div>
            <div className="so-muted" style={{ marginTop: 3 }}>
              {new Date(quote.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {!editing && (
              <button className="btn btn-outline" style={{ fontSize: '0.82rem', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setEditing(true)}>
                <FiEdit2 size={13} /> Edit
              </button>
            )}
            {editing && (
              <>
                <button className="btn btn-outline" style={{ fontSize: '0.82rem', padding: '6px 14px' }} onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                <button className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 5 }} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : <><FiCheck size={13} /> Save</>}
                </button>
              </>
            )}
            <button onClick={onClose} style={{ padding: 6, cursor: 'pointer', color: '#6b7280', display: 'flex' }}>
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* Status quick-change */}
        <div className="so-pill-group" style={{ marginBottom: 20 }}>
          {STATUS_OPTIONS.filter(s => s.value !== 'all').map(s => (
            <button
              key={s.value}
              onClick={() => handleStatusQuickChange(s.value)}
              className={`so-pill${quote.status === s.value ? ' so-pill--active' : ''}`}
              style={quote.status === s.value
                ? { background: s.color, borderColor: s.color, color: '#fff' }
                : { borderColor: s.color, color: s.color }
              }
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Customer info */}
        <div style={{ background: '#f9fafb', borderRadius: 10, padding: '14px 16px', marginBottom: 20, border: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280' }}>Customer Information</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px 20px' }}>
            {editing ? (
              <>
                <div><div style={labelStyle}>Name *</div><input style={inputStyle} value={editForm.customerName} onChange={setField('customerName')} /></div>
                <div><div style={labelStyle}>Phone *</div><input style={inputStyle} type="tel" value={editForm.customerPhone} onChange={setField('customerPhone')} /></div>
                <div><div style={labelStyle}>Email</div><input style={inputStyle} type="email" value={editForm.customerEmail} onChange={setField('customerEmail')} /></div>
                <div><div style={labelStyle}>Company</div><input style={inputStyle} value={editForm.customerCompany} onChange={setField('customerCompany')} /></div>
                <div><div style={labelStyle}>Expected Delivery</div><input style={inputStyle} type="date" value={editForm.expectedDeliveryDate} onChange={setField('expectedDeliveryDate')} /></div>
              </>
            ) : (
              <>
                <div><div style={labelStyle}><FiUser size={10} /> Name</div><div className="so-strong">{quote.customerName}</div></div>
                <div><div style={labelStyle}><FiPhone size={10} /> Phone</div><div style={{ fontSize: '0.88rem' }}>{quote.customerPhone}</div></div>
                {quote.customerEmail    && <div><div style={labelStyle}><FiMail size={10} /> Email</div><div style={{ fontSize: '0.88rem' }}>{quote.customerEmail}</div></div>}
                {quote.customerCompany  && <div><div style={labelStyle}><FiBriefcase size={10} /> Company</div><div style={{ fontSize: '0.88rem' }}>{quote.customerCompany}</div></div>}
                {quote.expectedDeliveryDate && <div><div style={labelStyle}><FiCalendar size={10} /> Delivery Date</div><div style={{ fontSize: '0.88rem' }}>{quote.expectedDeliveryDate}</div></div>}
              </>
            )}
          </div>
          {quote.customerMessage && !editing && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <div style={labelStyle}><FiMessageSquare size={10} /> Message</div>
              <div style={{ fontSize: '0.88rem', color: '#374151', lineHeight: 1.6 }}>{quote.customerMessage}</div>
            </div>
          )}
          {editing && (
            <div style={{ marginTop: 12 }}>
              <div style={labelStyle}>Customer Message</div>
              <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }} value={editForm.customerMessage} onChange={setField('customerMessage')} />
            </div>
          )}
        </div>

        {/* Items */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280' }}>Quote Items</div>
          {displayItems.map((item, idx) => (
            <div key={idx} style={{ border: '1px solid var(--border)', borderRadius: 9, padding: '12px 14px', marginBottom: 10, background: '#fff' }}>
              {editing ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px 14px' }}>
                  <div><div style={labelStyle}>Product Name</div><input style={inputStyle} value={item.productName} onChange={e => updateItem(idx, 'productName', e.target.value)} /></div>
                  <div><div style={labelStyle}>Variant Name</div><input style={inputStyle} value={item.variantName} onChange={e => updateItem(idx, 'variantName', e.target.value)} /></div>
                  <div><div style={labelStyle}>Quantity</div><input style={inputStyle} type="number" min={1} value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)} /></div>
                  <div><div style={labelStyle}>Unit Price (₹)</div><input style={inputStyle} type="number" step="0.01" value={item.unitPrice} onChange={e => updateItem(idx, 'unitPrice', parseFloat(e.target.value) || 0)} /></div>
                  <div><div style={labelStyle}>Total (₹)</div><input style={{ ...inputStyle, background: '#f3f4f6' }} value={(parseFloat(item.totalPrice) || 0).toFixed(2)} readOnly /></div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div className="so-strong">{item.productName}</div>
                    <div className="so-muted">{item.variantName} · SKU: {item.variantSku}</div>
                    {item.selectedOptions?.length > 0 && (
                      <div className="so-muted" style={{ marginTop: 2 }}>
                        {item.selectedOptions.map(o => `${o.optionGroupName}: ${o.optionValue}`).join(' · ')}
                      </div>
                    )}
                    <div style={{ fontSize: '0.82rem', marginTop: 4 }}>Qty: <strong>{item.quantity?.toLocaleString('en-IN')}</strong></div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '0.95rem' }}>{formatINR(parseFloat(item.totalPrice) || 0)}</div>
                    <div className="so-muted">₹{parseFloat(item.unitPrice || 0).toFixed(2)} / pc</div>
                  </div>
                </div>
              )}
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', fontWeight: 800, fontSize: '1rem', paddingTop: 8 }}>
            Total Estimate: <span style={{ color: 'var(--primary)', marginLeft: 8 }}>{formatINR(subtotal)}</span>
          </div>
        </div>

        {/* Admin Notes */}
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280' }}>Admin Notes (Internal)</div>
          {editing ? (
            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }} value={editForm.adminNotes} onChange={setField('adminNotes')} placeholder="Internal notes not visible to customer…" />
          ) : (
            <div style={{
              background: '#f9fafb', border: '1px solid var(--border)', borderRadius: 8,
              padding: '10px 12px', fontSize: '0.85rem',
              color: quote.adminNotes ? '#374151' : '#9ca3af', minHeight: 44,
            }}>
              {quote.adminNotes || 'No admin notes yet.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main AdminQuoteRequests
// ─────────────────────────────────────────────────────────────────────
export default function AdminQuoteRequests() {
  const [quotes,      setQuotes]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [pages,       setPages]       = useState(1);
  const [search,      setSearch]      = useState('');
  const [status,      setStatus]      = useState('all');
  const [startDate,   setStartDate]   = useState('');
  const [endDate,     setEndDate]     = useState('');
  const [selected,    setSelected]    = useState(null);
  const LIMIT = 15;

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (status !== 'all') params.set('status', status);
      if (search)    params.set('search', search);
      if (startDate) params.set('startDate', startDate);
      if (endDate)   params.set('endDate', endDate);
      const res = await API.get(`/public-quotes/admin?${params}`);
      if (res.data.success) {
        setQuotes(res.data.quotes);
        setTotal(res.data.total);
        setPages(res.data.pages);
      }
    } catch {
      toast.error('Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  }, [page, status, search, startDate, endDate]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  // Debounced search — triggers fetch 350ms after user stops typing (same as SalesOrders)
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdated = (updated) => {
    setQuotes(prev => prev.map(q => q.id === updated.id ? updated : q));
    if (selected?.id === updated.id) setSelected(updated);
  };

  return (
    <div className="so-page">
      {/* Page head */}
      <div className="so-page-head">
        <div>
          <h2>Quote Requests</h2>
          <div className="so-page-sub">{total} total · Customer enquiries from the public website</div>
        </div>
      </div>

      {/* Filter bar — all controls on one line */}
      <div className="so-filter-bar">
        <div className="so-search-input so-search-input--filter" style={{ flex: 1 }}>
          <FiSearch size={15} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Name, phone, ref no…"
          />
        </div>

        <div className="so-filter-dates">
          <input type="date" className="so-date-input" value={startDate}
            onChange={e => { setStartDate(e.target.value); setPage(1); }}
            onClick={e => e.target.showPicker && e.target.showPicker()} />
          <span className="so-date-sep">–</span>
          <input type="date" className="so-date-input" value={endDate}
            onChange={e => { setEndDate(e.target.value); setPage(1); }}
            onClick={e => e.target.showPicker && e.target.showPicker()} />
        </div>

        <div className="so-pill-group">
          {STATUS_OPTIONS.map(s => (
            <button
              key={s.value}
              onClick={() => { setStatus(s.value); setPage(1); }}
              className={`so-pill${status === s.value ? ' so-pill--active' : ''}`}
              style={status === s.value
                ? { background: s.color, borderColor: s.color, color: '#fff' }
                : { borderColor: s.color, color: s.color }
              }
            >
              {s.label}
            </button>
          ))}
        </div>

        {(search || startDate || endDate) && (
          <button
            className="so-clear-btn"
            onClick={() => { setSearch(''); setStartDate(''); setEndDate(''); setPage(1); }}
          >
            <FiX size={13} /> Clear
          </button>
        )}
      </div>

      {/* Table card */}
      <div className="so-card so-card--table">
        {loading ? (
          <div className="so-empty">Loading…</div>
        ) : quotes.length === 0 ? (
          <div className="so-empty">No quote requests found.</div>
        ) : (
          <div className="so-table-scroll">
            <table className="so-table">
              <thead>
                <tr>
                  {['Reference', 'Customer', 'Items', 'Subtotal', 'Status', 'Date'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {quotes.map(q => (
                  <tr key={q.id} onClick={() => setSelected(q)}>
                    <td className="so-strong" style={{ color: 'var(--primary)', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {q.referenceNumber}
                    </td>
                    <td>
                      <div className="so-strong">{q.customerName}</div>
                      <div className="so-muted">{q.customerPhone}</div>
                      {q.customerCompany && <div className="so-muted">{q.customerCompany}</div>}
                    </td>
                    <td className="so-muted" style={{ whiteSpace: 'nowrap' }}>
                      {(q.items || []).length} item{(q.items || []).length !== 1 ? 's' : ''}
                    </td>
                    <td style={{ fontWeight: 700, color: 'var(--primary)', whiteSpace: 'nowrap' }}>
                      {formatINR(parseFloat(q.subtotal) || 0)}
                    </td>
                    <td><StatusBadge status={q.status} /></td>
                    <td className="so-muted" style={{ whiteSpace: 'nowrap' }}>
                      {new Date(q.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            <FiChevronLeft size={14} /> Prev
          </button>
          <span className="so-muted">Page {page} of {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
            className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 4 }}>
            Next <FiChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <QuoteDetailModal
          quote={selected}
          onClose={() => setSelected(null)}
          onUpdated={handleUpdated}
        />
      )}
    </div>
  );
}